import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { MAT_CHECKBOX_CLICK_ACTION } from '@angular/material';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';

import { IDataset } from '../../models/dataset';
import { DatasetType } from '../../models/dataset-type';
import { IEventType } from '../../models/events-data';
import { DEFAULT_MENU_OPTIONS, IMenuOptions } from '../../models/menu-options';
import { DEFAULT_UI_OPTIONS, IUiOptions } from '../../models/ui-options';
import { DatasetService } from '../../services/dataset.service';
import { EventsService } from '../../services/events.service';
import { MenuOptionsService } from '../../services/menu-options.service';
import { PlotsService } from '../../services/plots.service';
import { UiOptionsService } from '../../services/ui-options.service';

/** Component for selecting various user options on the plot */
@Component({
    selector: 'sci-menu',
    templateUrl: './menu.component.html',
    styleUrls: [ './menu.component.scss' ],
    providers: [
        { provide: MAT_CHECKBOX_CLICK_ACTION, useValue: 'noop' } // requires us to manually update checkbox models
    ]
})
export class MenuComponent implements AfterViewInit, OnDestroy {
    @Input() disabled: boolean;
    @Input() plotId: string;
    options: IMenuOptions = cloneDeep( DEFAULT_MENU_OPTIONS );
    datasets: IDataset[] = [];
    events: IEventType[] = [];
    uiOptions: IUiOptions = cloneDeep( DEFAULT_UI_OPTIONS );
    useGlobalSettings: boolean;
    activeDataset: IDataset; // which dataset should be used to define limits and other features
    DatasetType = DatasetType; // make the enum available to the html template
    plotList: { title: string, id: string }[] = [];
    subscriptions: Subscription[] = [];

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private plotsService: PlotsService,
        private _datasetService: DatasetService,
        private _menuOptionsService: MenuOptionsService,
        private _uiOptionsService: UiOptionsService,
        private _eventsService: EventsService
    ) {}

    ngAfterViewInit() {
        setTimeout( () => {
            this.subscriptions.push( this.plotsService.getPlots$().subscribe( plots => {
                if ( !this.disabled ) {
                    this.plotList = [];
                    plots.forEach( plot => {
                        if ( plot.plotId !== this.plotId && this.plotsService.canCombine( plot.plotId, this.plotId ) ) {
                            this.plotList.push({
                                title: plot.title,
                                id: plot.plotId
                            });
                        }
                    });
                }
            }));

            this.subscriptions.push( this._datasetService.getPlotDatasets$( this.plotId ).subscribe( newDatasets => {
                this.datasets = newDatasets.datasets;
                if ( this.options.overplot.limitsDSID ) {
                    this.activeDataset = this.datasets.find( dataset => this.options.overplot.limitsDSID === dataset.uid );
                } else if ( this.datasets.length > 0 ) {
                    this.options.overplot.limitsDSID = this.datasets[0].uid;
                    this.activeDataset = this.datasets[0];
                }
                if ( this.datasets[0].eventsUrl != null ) {
                    this._eventsService.getPlotEvents$( this.plotId ).subscribe( events => {
                        this.events = events.types;
                    });
                }
            }));

            this.subscriptions.push( this._menuOptionsService.getMenuOptions$( this.plotId ).subscribe(
                newMenuOptions => {
                    this.options = newMenuOptions;
                    if ( this.options.useGlobalSettings ) {
                        const globalEvents = this._eventsService.allEventTypes.getValue();
                        this.events = this.events.map( event => {
                            event.checked = globalEvents.find( globalEvent => globalEvent.id === event.id ).checked;
                            return event;
                        });
                    }
                }
            ));

            this.subscriptions.push( this._uiOptionsService.getUiOptions$().subscribe( newUIOptions => {
                this.uiOptions = newUIOptions;
                this.changeDetectorRef.detectChanges();
            }));
        }, 0 );
    }

    ngOnDestroy() {
        this.subscriptions.forEach( subscription => subscription.unsubscribe() );
    }

    /** determines whether the active dataset has defined limits */
    hasDefinedLimits() {
        if ( !this.activeDataset ) {
            return false;
        }
        return this.activeDataset.metadata && this.activeDataset.metadata.limits.exist;
    }

    hasOffset() {
        return this.datasets.every( dataset => dataset.offset === 0 );
    }

    /** returns the type of the active dataset */
    getDataType() {
        if ( !this.activeDataset ) {
            return DatasetType.analog;
        }
        return this.activeDataset.type;
    }

    updateSelectedEvents() {
        this.options.view.eventTypes = this.events.filter( event => event.checked )
                                                  .map( event => event.id );
        this.updateMenuOptions();
    }

    updateMenuOptions() {
        this.forceChangeDetection();
        this._menuOptionsService.setMenuOptions( this.plotId, this.options );
    }

    /**
     * Generic function for toggling a boolean value on a nested object.
     * @param option - a list representing the location of the property to update.
     * Example
     * options: {
     *    toggle: {
     *        booleanToggle: false;
     *    }
     * }
     * would be represented as ['toggle', 'booleanToggle']
     */
    toggleOption( option: string[] ) {
        if ( option.length === 1 ) {
            this.options[ option[0] ] = !this.options[ option[0] ];
        } else if ( option.length === 2 ) {
            this.options[ option[0] ][ option[ 1 ] ] = !this.options[ option[0] ][ option[ 1 ] ];
        } else {
            console.error('Toggling options nested greater than 2 levels not yet supported. Needs to be implemented by a developer');
        }
        this.updateMenuOptions();
    }

    /** enable or disable global options for this plot */
    toggleGlobalOptions() {
        this._menuOptionsService.toggleGlobalOptions( this.plotId );
    }

    /**
     * For some reason mat-menu's have lazy change detection. This causes things like checkboxes
     * or radio buttons to not reflec the current value of their models. We can force change detection
     * with detectChanges(). We have to wrap this in a 0 value timeout so that this change detection runs
     * on the next available cycle as opposed to the current one which might not yet have our updated value
     */
    forceChangeDetection() {
        setTimeout( () => this.changeDetectorRef.detectChanges(), 0 );
    }

    triggerModifyDatasets() {
        // this.modifyDatasets.emit('modify');
    }

    splitPlot() {
        this.plotsService.splitPlot( this.plotId );
    }

    combinePlotWith( id: string ) {
        this.plotsService.combinePlots( this.plotId, id );
    }
}
